'use client';
import Button, { ButtonProps, Size } from '@/components/Button/Button';
import Checkbox from '@/components/Checkbox';
import ChevronExpand from '@/components/icons/ChevronExpand';
import CrossCircleFill from '@/components/icons/CrossCircleFill';
import Edit from '@/components/icons/Edit';
import FilterIcon from '@/components/icons/Filter';
import {
  ColumnDef,
  ColumnFiltersState,
  RowData,
  SortingState,
  TableOptions,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  JSXElementConstructor,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Loader from '../Loader';
import Filter from './Filter/Filter';
import Paginator from './Paginator/Paginator';
import styles from './table.module.css';

export type HeaderType = {
  filtrable: boolean;
  triable: boolean;
  type_filtre?: 'input' | 'choice';
  name: string;
  choices?: { label: string }[];
};

export type ButtonType = {
  button: ReactElement<ButtonProps, string | JSXElementConstructor<any>>;
  isFilter?: boolean;
};

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    filterVariant?: 'text'; // | 'range' | 'select' | 'date';
  }
}

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    updateData: (rowIndex: number, columnId: string, value: unknown) => void;
  }
}

export type TableProps<T> = {
  title?: string;
  columns: ColumnDef<T>[];
  columnsSort?: SortingState;
  data: T[];
  enableFilter: boolean;
  isLoadingData: boolean;

  leftbtns?: ButtonProps[];
  rightbtns?: ButtonProps[];

  onSelectionChange: (rows: T[]) => void;

  onEdit?: (row: T) => void;
};

export type PartialRowData = { disable?: boolean };

const Table = <TRowData extends PartialRowData>({
  title,
  columns,
  columnsSort,
  data,
  enableFilter,
  isLoadingData = false,
  leftbtns = [],
  rightbtns = [],
  onSelectionChange,
  onEdit,
}: TableProps<TRowData>): ReactNode => {
  const [size, setSize] = useState<Size>('medium');

  const [showFilters, setShowFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0, //initial page index
    pageSize: 10, //default page size
  });

  const cols = useMemo(() => {
    const columnHelper = createColumnHelper<TRowData>();
    const res = [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onClick={table.getToggleAllPageRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            disabled={row.original.disable}
            onClick={row.getToggleSelectedHandler()}
          />
        ),
        enableColumnFilter: false,
        enableSorting: false,
      },
      ...columns,
    ] as ColumnDef<TRowData>[];

    onEdit &&
      res.push(
        columnHelper.display({
          cell: (props) => (
            <Edit
              className={styles['edit-icon']}
              onClick={() => {
                !props.row.original.disable && onEdit(props.row.original);
              }}
            />
          ),
          id: 'edit',
          header: () => 'Actions',
        }) as ColumnDef<TRowData>
      );

    return res;
  }, [onEdit]);

  useEffect(() => {
    table.toggleAllPageRowsSelected(false);
  }, [columnFilters, pagination]);

  useEffect(() => {
    if (columnsSort) setSorting([...columnsSort]);
  }, []);

  useEffect(() => {
    onSelectionChange(getSelectedValues());
  }, [selectedRows]);

  useEffect(() => {
    setSelectedRows({});
  }, [data]);

  useEffect(() => {
    if (!showFilters) {
      table
        .getHeaderGroups()
        .map((headerGroup) =>
          headerGroup.headers.map(
            (header) =>
              header.column.getCanFilter() && header.column.setFilterValue('')
          )
        );
    }
  }, [showFilters]);

  const options: TableOptions<TRowData> = {
    data: data,
    columns: cols,
    filterFns: {},
    enableSortingRemoval: false,
    state: {
      rowSelection: selectedRows,
      columnFilters: columnFilters,
      pagination: pagination,
      sorting: sorting,
    },
    enableRowSelection: (row) => !row?.original?.disable,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onRowSelectionChange: setSelectedRows,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination, //update the pagination state when internal APIs mutate the pagination state
  };

  const table = useReactTable(options);

  const getSelectedValues = () => {
    return table
      .getSelectedRowModel()
      .rows.map<TRowData>((row) => row.original);
  };

  useEffect(() => {
    if (window.innerWidth >= 768) {
      if (size != 'medium') setSize('medium');
    } else {
      if (size != 'small') setSize('small');
    }

    window.addEventListener('resize', (event) => {
      if (window.innerWidth >= 768) {
        if (size != 'medium') setSize('medium');
      } else {
        if (size != 'small') setSize('small');
      }
    });
    return () => {
      window.removeEventListener('resize', () => {});
    };
  }, [size]);

  return (
    <>
      <div className={`flex-column gap-xl ${styles.content}`}>
        <h3 className="bigger">{title}</h3>
        <div className={styles['header-buttons']}>
          <div className="flex-row gap-m">
            {leftbtns.map((btnProps, index) => (
              <Button key={index} {...btnProps} size={size} />
            ))}
          </div>
          <div className="flex-row gap-m">
            {enableFilter && (
              <Button
                variant="secondary"
                onClick={() => setShowFilters(!showFilters)}
                size={size}
                icon={<FilterIcon />}
              >
                Filtrer
              </Button>
            )}
            {rightbtns.map((btnProps, index) => (
              <Button key={index} {...btnProps} size={size} />
            ))}
          </div>
        </div>
        <div className={styles['table-container']}>
          <table className={styles.table}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      className={
                        header.column.getCanSort() ? styles['sortable'] : ''
                      }
                    >
                      {header.column.getCanSort() && <ChevronExpand />}
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}

              {showFilters &&
                table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {header.column.getCanFilter() && (
                          <Filter column={header.column} />
                        )}
                        {header.id === 'edit' && (
                          <CrossCircleFill
                            className={styles['hide-filters']}
                            onClick={() => setShowFilters(false)}
                          />
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={row.original.disable ? styles['disabled-row'] : ''}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <Paginator
            className={styles.paginator}
            max={table.getPageCount()}
            pageIndex={pagination.pageIndex}
            onChange={(pageIndex) =>
              setPagination({ ...pagination, pageIndex: pageIndex })
            }
            hasNext={table.getCanNextPage()}
            hasPrev={table.getCanPreviousPage()}
          />
        </div>
        {isLoadingData && (
          <div className={styles['spinner']}>
            <Loader size="XXL" />
          </div>
        )}
      </div>
    </>
  );
};

export default Table;
